import compose from 'recompose/compose';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import moment from 'moment-timezone';
import Autosuggest from 'react-autosuggest';
import debounce from 'lodash/debounce';
import find from 'lodash/find';
import groupBy from 'lodash/groupBy';
import get from 'lodash/get';
import map from 'lodash/map';
import { EventEmitter } from 'events';
import { getAppointment } from '../appointments/actions';
import config from '../../../config';
import Icon, { GLYPHS } from '../../common/icon';
import {
  clearResults,
} from './actions';
import withCurrency from '../../common/hoc/withCurrency';
import { containerApp } from '../../../main';
import {
  getSearchResults,
  isSearchFetching,
} from './selectors';
import withPermissions from '../../auth/utils/withPermissions';
import { canUserViewAppointmentContactInfo } from '../../common/helpers/users';
import { maskEmail, maskPhoneNumber } from '../../common/helpers/mask';
import { t } from '../../../modules/infra/i18n';

class GlobalSearchBox extends Component {
  constructor(props) {
    super(props);
    this.state = {
      searchValue: '',
    };

    this.onSuggestionsFetchRequested = debounce(({ value }) => this.props.performSearch(value, this.props.type), 300);
  }

  formatSuggestionString(hit) {
    const getArrayValue = (hit, key) => {
      const highlight = find(hit.highlights, { field: key });
      if (highlight && highlight.indices) {
        return get(hit.document, [key, highlight.indices[0]], '');
      }
      return get(hit.document, [key, 0], '');
    };
    const hl = (hit, key, formatter) => {
      const value = get(hit.document, key);
      const highlight = find(hit.highlights, { field: key });
      if (highlight) {
        if (Array.isArray(value)) {
          return formatter ? formatter(highlight.snippets[0]) : highlight.snippets[0];
        }
        return formatter ? formatter(highlight.snippet) : highlight.snippet;
      }
      if (Array.isArray(value)) {
        return formatter ? formatter(get(value, 0, '')) : get(value, 0, '');
      }
      return formatter ? formatter(value) : value;
    };
    const color = hit =>
      /*
       * color disc for barber
       */
      hit.document.color ? `<b style="color: ${hit.document.color};">&#x25CF;</b> ` : '';
    const dateTime = hit => moment(hit.document.dateTime).tz(this.props.timezone).format('ddd, MMM D, hh:mm a');
    switch (hit.document.type) {
      case 'appointment':
        // (color disc) haircut for Joe Heart by Micky Rurke at Wed, Sep 23, 01:30 am
        if (!(hit.document.services && hit.document.customer && hit.document.barber && hit.document.dateTime)) {
          return false;
        }
        let isCanceled = '';
        if (hit.document.status === 1) {
          isCanceled = ` - ${t('reports:appointment_status_filter.cancelled')}`;
        }
        return {
          id: hit.document.id,
          type: hit.document.type,
          value: `${hit.document.services} for ${hit.document.customer} by ${hit.document.barber} at ${dateTime(hit)}${isCanceled}`,
          highlight: `${color(hit) + hl(hit, 'services')} for ${hl(hit, 'customer')} by ${hl(hit, 'barber')} at ${dateTime(hit)}${isCanceled}`,
        };
      case 'barber':
        // (color disc) Micky Rurke, +771188314346, Alize26@gmail.com
        if (!hit.document.name) {
          return false;
        }
        return {
          // `barberId` represents brand-level barber and `id` represents a shop-level barber profile id
          // thus respective fields must be used for different user kinds
          id: this.props.userKind === 'brand' ? hit.document.barberId : hit.document.id,
          type: hit.document.type,
          value: `${hit.document.name}, ${hit.document.phone}, ${hit.document.email}`,
          highlight: `${color(hit) + hl(hit, 'name')}, ${hl(hit, 'phone')}, ${hl(hit, 'email')}`,
        };
      case 'customer':
        // Joe Heart, +771188314346, Alize26@gmail.com
        if (!hit.document.name) {
          return false;
        }
        let phoneAndEmail = `, ${hit.document.phone}, ${hit.document.email}`;
        let phoneAndEmailHighlight = `, ${hl(hit, 'phone')}, ${hl(hit, 'email')}`;
        if (this.props.type === 'appointment' && !canUserViewAppointmentContactInfo(this.props.permissions)) {
          phoneAndEmail = `, ${maskPhoneNumber(hit.document.phone)}, ${maskEmail(hit.document.email)}`;
          phoneAndEmailHighlight = `, ${maskPhoneNumber(hit.document.phone)}, ${maskEmail(hit.document.email)}`;
        }
        return {
          id: hit.document.id,
          type: hit.document.type,
          value: `${hit.document.name}${phoneAndEmail}`,
          highlight: `${hl(hit, 'name')}${phoneAndEmailHighlight}`,
        };
      case 'adminCustomer':
        // Joe Heart, +771188314346, Alize26@gmail.com
        if (!hit.document.username) {
          return false;
        }
        return {
          id: hit.document.id,
          type: hit.document.type,
          value: `${hit.document.username} ${get(hit.document, 'customer.name')}, ${get(hit.document, 'customer.phone')}, ${get(hit.document, 'customer.email')}`,
          highlight: `${hl(hit, 'username')}, ${hl(hit, 'customer.name')}, ${hl(hit, 'customer.phone')}, ${hl(hit, 'customer.email')}`,
        };
      case 'service':
        // haircut
        if (!hit.document.name) {
          return false;
        }
        return {
          id: hit.document.id,
          type: hit.document.type,
          value: `${hit.document.name}`,
          highlight: `${hl(hit, 'name')}`,
        };
      case 'brand':
        return {
          id: hit.document.id,
          type: hit.document.type,
          value: hit.document.name,
          highlight: `${hl(hit, 'name')}`,
        };
      case 'shop':
        return {
          id: hit.document.id,
          type: hit.document.type,
          value: `${hit.document.name}, ${get(hit.document, 'address.city', '')}, ${get(hit.document, 'phone', '')}, ${get(hit.document, 'email', '')}`,
          highlight: `${hl(hit, 'name')}, ${hl(hit, 'address.city')}, ${hl(hit, 'phone')}, ${hl(hit, 'email')}`,
        };
      case 'user':
        return {
          id: hit.document.id,
          type: hit.document.type,
          value: `${hit.document.username} - ${getArrayValue(hit, 'shopNames')} - ${get(hit.document, 'userType', '')}`,
          highlight: `${hl(hit, 'username')} - ${hl(hit, 'shopNames')} - ${hl(hit, 'userType')}`,
          dataAutomationId: `${hit.document.username}-userSearchResult`,
        };
      case 'product':
        return {
          id: hit.document.id,
          type: hit.document.type,
          value: hit.document.name,
          highlight: `${hl(hit, 'name')}`,
        };
      default:
        return false;
    }
  }

  getSuggestions(hits) {
    const suggestions = [];

    if (hits.length) {
      map(groupBy(hits, 'document.type'), (hitGroup, title) => {
        const group = [];
        hitGroup.forEach((hit) => {
          const element = this.formatSuggestionString(hit);
          if (element) {
            group.push(element);
          }
        });
        // if group.length
        suggestions.push({
          title,
          hits: group,
        });
      });
    } else {
      suggestions.push({
        title: t('admin:users.list.no_results_found'),
        hits: [''],
        empty: true,
      });
    }
    return suggestions;
  }

  getSuggestionValue(suggestion) {
    return `${suggestion.value}`;
  }

  getSectionSuggestions(section) {
    return section.hits;
  }

  renderSuggestion(suggestion) {
    const createMarkup = html => ({ __html: html });
    return (
      <span
        data-automation-id={suggestion.dataAutomationId}
        dangerouslySetInnerHTML={createMarkup(suggestion.highlight)}
      />
    );
  }

  renderSectionTitle(section) {
    const translatedTitle = section.empty 
      ? section.title 
      : t(`common:search.sections.${section.title}`, { defaultValue: section.title });
    return (
      <strong>{translatedTitle}</strong>
    );
  }

  getUrl(url) {
    return `${this.props.urlPrefix}${url}`;
  }

  onSuggestionSelected(event, { suggestion }) {
    const storeUrl = `/admin/shops/${suggestion.id}`;

    switch (suggestion.type) {
      case 'appointment':
        this.props.getAppointment(suggestion.id).then((appointment) => {
          EventEmitter.prototype.emit('appointment-clicked', appointment);
          EventEmitter.prototype.emit('open-appointment-modal', 'details');
        });
        break;
      case 'brand':
        const brandUrl = `/admin/brands/${suggestion.id}`;
        this.props.router.push(brandUrl);
        break;
      case 'barber':
        const barberUrl = this.getUrl(`/barbers/${suggestion.id}`);
        this.props.router.push(barberUrl);
        break;
      case 'customer':
        const customerUrl = this.getUrl(`/customers/${suggestion.id}`);
        this.props.router.push(customerUrl);
        break;
      case 'service':
        const serviceUrl = this.getUrl(`/services/${suggestion.id}`);
        this.props.router.push(serviceUrl);
        break;
      case 'shop':
        this.props.router.push(storeUrl);
        break;
      case 'user':
        const userUrl = `/admin/users/${suggestion.id}`;
        this.props.router.push(userUrl);
        break;
      case 'adminCustomer':
        const adminCustomerUrl = `/admin/customers/${suggestion.id}`;
        this.props.router.push(adminCustomerUrl);
        break;
      case 'product':
        const productUrl = this.getUrl(`/products/${suggestion.id}/edit`);
        this.props.router.push(productUrl);
        break;
    }
    this.setState({
      searchValue: '',
    });
    this.props.clearResults();
  }

  onChange(e, { newValue }) {
    this.setState({
      searchValue: newValue,
    });
  }

  onKeyDown(e) {
    const keyCode = e.keyCode ? e.keyCode : e.charCode;
    if (keyCode === config.ENTER_KEY_CODE) {
      e.preventDefault();
    }
  }

  onSuggestionsClearRequested() {
    this.setState({
      searchSuggestions: [],
    });
  }

  render() {
    const { searchValue, noResults } = this.state;
    const searchInputProps = {
      value: searchValue,
      onChange: this.onChange.bind(this),
      onKeyDown: this.onKeyDown.bind(this),
      className: 'c-input c-input--search',
      placeholder: t('admin:users.list.search_placeholder'),
    };
    let icon;
    if (this.props.isFetching) {
      icon = (
        <div className="c-search__loader">
          <div className="c-search__loader-spin" />
        </div>
      );
    } else {
      icon = (
        <div className="c-search__icon">
          <Icon glyph={GLYPHS.search} size="medium" />
        </div>
      );
    }

    return (
      <div className="c-form-input-group">
        <div className="c-form-input-group__item c-form-input-group__item--fill">
          <div className="c-search c-search--global" data-automation-id="search-box">
            <Autosuggest
              multiSection
              suggestions={this.getSuggestions(this.props.results)}
              onSuggestionsFetchRequested={this.onSuggestionsFetchRequested}
              onSuggestionsClearRequested={this.onSuggestionsClearRequested.bind(this)}
              getSuggestionValue={this.getSuggestionValue}
              renderSuggestion={this.renderSuggestion}
              renderSectionTitle={this.renderSectionTitle}
              onSuggestionSelected={this.onSuggestionSelected.bind(this)}
              getSectionSuggestions={this.getSectionSuggestions}
              inputProps={searchInputProps}
            />
            {icon}
          </div>
        </div>
      </div>
    );
  }
}

GlobalSearchBox.propTypes = {
  timezone: PropTypes.string,
  performSearch: PropTypes.func.isRequired,
  getAppointment: PropTypes.func.isRequired,
  results: PropTypes.array,
  isFetching: PropTypes.bool,
  urlPrefix: PropTypes.string,
  clearResults: PropTypes.func.isRequired,
  currency: PropTypes.string,
  permissions: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  userKind: PropTypes.oneOf(['barber', 'shop', 'brand']).isRequired,
};

GlobalSearchBox.defaultTypes = {
  results: [],
  currency: 'usd',
};

const mapStateToProps = state => ({
  timezone: state.auth.timezone,
  results: getSearchResults(state),
  isFetching: isSearchFetching(state),
});

const mapDispatchToProps = {
  getAppointment,
  clearResults,
};

const SharedGlobalSearchBoxContainer = compose(
  connect(mapStateToProps, mapDispatchToProps),
  withPermissions,
  withCurrency,
  withRouter,
);

export default compose(
  SharedGlobalSearchBoxContainer,
  containerApp.hideForContainerApp,
)(GlobalSearchBox);

export const VisibleInContainerGlobalSearchBox = SharedGlobalSearchBoxContainer(GlobalSearchBox);
